'use client';

import { sendCustomerEvent } from '@/components/newRelic';
import { showToast } from '@/redux/navigationSlice';
import { pushUserdata } from '@/redux/userdataSlice';
import { customerJWT, login, loginResponse } from '@repo/customer-api';
import { deleteClientCookie, formatter, parseJwt, setClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { DynamicLogin } from '../components/DynamicLogin';

/**
 * Fetch and cache offers asynchronously without blocking form submission
 */
async function fetchAndCacheOffers(): Promise<void> {
  try {
    // disabled for now
    //const { ratings } = await offers();
    //setClientCookie('offers', JSON.stringify(ratings));
  } catch (error) {
    console.error('Failed to cache offers:', error);
    // Silently fail - offers cache is optional for UX
  }
}

export default function Page(): React.JSX.Element {
  const router = useRouter();
  const dispatch = useDispatch();

  useEffect(() => {
    deleteClientCookie(['offers', 'token-customer', 'userId-customer', 'username-customer']);
  }, []);

  // Memoized activation button click handler
  const handleActivatAccount = useCallback(() => {
    router.push('/retrieve_activation');
  }, [router]);

  // Memoized login callback
  const handleLoginSuccess = useCallback(
    async (response: loginResponse) => {
      // Decode token and extract user info
      const decodedToken = parseJwt(response.token) as customerJWT;
      const expireDate = new Date(decodedToken.exp * 1000).toUTCString();

      // Set authentication cookies (blocking, critical path)
      setClientCookie('token-customer', response.token, expireDate);
      setClientCookie('userId-customer', `${decodedToken.userId}`);

      // Update Redux state with user data (blocking, critical path)
      dispatch(
        pushUserdata({
          username: formatter(decodedToken.fullName, 'capitalize').replaceAll(',', ' '),
          userId: `${decodedToken.userId}`,
        }),
      );

      // Send analytics event
      sendCustomerEvent({ type: 'interaction', subtype: 'login' });

      // Fire off offers fetch in the background (non-blocking)
      // This allows form submission feedback to show immediately
      fetchAndCacheOffers().then(
        () => {
          // Navigate after critical operations complete
          router.push('/loans/operations');
        },
        (error) => {
          console.error('Navigation error:', error);
          // Still navigate even if offers fetch fails
          router.push('/loans/operations');
        },
      );
    },
    [router, dispatch],
  );

  // Memoized forgot password handler
  const handleForgotPassword = useCallback(() => {
    router.push('/forgot_password');
  }, [router]);

  // Memoized error handler
  const handleLoginError = useCallback(() => {
    dispatch(showToast({ kind: 'error', text: 'Usuario y/o contraseña incorrectos' }));
  }, [dispatch]);

  return (
    <DynamicLogin
      connector={login}
      extraButtons={[
        {
          label: 'Activar mi cuenta',
          onClick: handleActivatAccount,
        },
      ]}
      callback={handleLoginSuccess}
      forgotPassword={handleForgotPassword}
      onError={handleLoginError}
    />
  );
}
